import { defineStore } from "pinia";
import {
  addressResponse,
  bankInfoResponse,
  userDetailResponse,
  userSnsAccountResponse,
} from "../types/apiResponse";
import {
  addressRegistRequest,
  bankInfoRequest,
  updateUserSnsAccountRequest,
} from "../types/apiRequest";
import {
  deleteAddressInfo,
  deleteBankInfo,
  deleteUserSnsInfo,
  getAddressInfo,
  getBankInfo,
  getUserDetail,
  getUserSnsInfo,
  signIn,
  signUp,
  updateAddressInfo,
  updateBankInfo,
  updateUserInfo,
  updateUserSnsInfo,
  withdrawal,
} from "../services/UserInfoService";
import { CommonStore } from "./CommonStore";
import { MEDIA_NAME } from "../consts/common";

const userDetail: userDetailResponse = {
  id: 0,
  name: "",
  first_name: "",
  last_name: "",
  birthday: "",
  email: "",
  user_state: "unregistered",
};

const bankInfoResponse: bankInfoResponse = {
  id: 0, //銀行口座ID
  bank_account_name: "", //口座名義
  bank_branch: "", //支店名
  bank_code: "", //口座番号
  bank_name: "", //銀行名
  bank_type: 0, //口座種別 普通:0, 当座: 1
  account_type: 0, //種別 個人:0, 法人: 1
};

const addressResponse: addressResponse = {
  id: -1,
  postal_code: "", //郵便番号
  prefecture: "", //都道府県
  city: "", //市区町村
  street: "", //番地以降
  building: "", //ビル名など
  phone_number: "", //電話番号
  name: "", //名前
};

export const UserInfoStore = defineStore("user", {
  state: () => {
    return {
      _user_detail: userDetail,
      _bankInfo: bankInfoResponse,
      _userSnsInfo: new Array<userSnsAccountResponse>(),
      _isRegistFromProjectDetail: { show: false, title: "", mediaType: -1 },
      _addressInfo: addressResponse,
    };
  },

  getters: {
    // ユーザー詳細
    userDetail(): userDetailResponse {
      return this._user_detail;
    },
    // 銀行口座情報
    bankInfo(): bankInfoResponse {
      return this._bankInfo;
    },
    userSnsInfo(): userSnsAccountResponse[] {
      return this._userSnsInfo;
    },
    isRegistFromProjectDetail(): {
      show: boolean;
      title: string;
      mediaType: number;
    } {
      return this._isRegistFromProjectDetail;
    },
    // 配送先住所情報
    addressInfo(): addressResponse {
      return this._addressInfo;
    },
  },

  actions: {
    // ユーザー情報取得
    async getUserDetail() {
      await signIn()
        .then(async () => {
          const res = await getUserDetail();
          this.setUserDetail(res);
        })
        .catch((e) => {
          console.log(e);
        });
    },

    // 新規登録
    // lineのアクセストークンの情報だけ登録する（ユーザー名等の登録はユーザー情報更新APIから）
    async signUp() {
      await signUp();
    },
    // ユーザー情報更新
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async updateUserInfo(params: any): Promise<userDetailResponse | undefined> {
      const commonStore = CommonStore();
      return await updateUserInfo(params).then((res) => {
        if (res) {
          commonStore.setToast({
            showToast: true,
            message:
              document.title == "プロフィール更新"
                ? "ユーザー情報を更新しました。"
                : "ユーザー情報を登録しました。",
            state: 0,
            timeout: 2000,
          });
          this.setUserDetail(res);
        } else {
          commonStore.setToast({
            showToast: true,
            message:
              document.title == "プロフィール更新"
                ? "ユーザー情報更新に失敗しました。"
                : "ユーザー情報登録に失敗しました。",
            state: 1,
            timeout: 2000,
          });
        }
        return res;
      });
    },
    // 退会
    async withdrawal(
      reason: string
    ): Promise<{ result: boolean; message: string }> {
      const commonStore = CommonStore();
      const result = await withdrawal(reason);
      if (result) {
        commonStore.setToast({
          showToast: true,
          message: result.message,
          state: result.state,
          timeout: 2000,
        });
      }
      return result;
    },
    // 銀行口座情報取得
    async getBankInfo() {
      const res = await getBankInfo();
      this.setbankInfo(res);
    },
    // 銀行口座情報更新
    async updateBankInfo(
      req: bankInfoRequest
    ): Promise<bankInfoResponse | undefined> {
      const commonStore = CommonStore();
      return await updateBankInfo(req).then((res) => {
        if (res) {
          commonStore.setToast({
            showToast: true,
            message:
              document.title == "振込先口座情報更新"
                ? "銀行口座情報を更新しました。"
                : "銀行口座情報を登録しました。",
            state: 0,
            timeout: 2000,
          });
          // ストアの情報を更新しておく
          this.setbankInfo(res);

          return res;
        } else {
          commonStore.setToast({
            showToast: true,
            message:
              document.title == "振込先口座情報更新"
                ? "銀行口座情報の更新に失敗しました。"
                : "銀行口座情報の登録に失敗しました。",
            state: 1,
            timeout: 2000,
          });

          return res;
        }
      });
    },
    // 銀行口座情報削除
    async deleteBankInfo(): Promise<boolean> {
      const commonStore = CommonStore();
      return await deleteBankInfo().then((res) => {
        if (res.status == 200) {
          commonStore.setToast({
            showToast: true,
            message: "銀行口座情報を削除しました。",
            state: 0,
            timeout: 2000,
          });
          // ストアの情報を更新しておく
          this.setbankInfo(undefined);

          return true;
        } else {
          commonStore.setToast({
            showToast: true,
            message: res.error.message || "銀行口座情報の削除に失敗しました。",
            state: 1,
            timeout: 2000,
          });

          return false;
        }
      });
    },
    // SNS アカウント情報取得
    async getUserSnsAccount() {
      const res = await getUserSnsInfo();
      this.setUserSnsInfo(res);
    },
    // SNS アカウント情報更新
    async updateUserSnsAccount(req: updateUserSnsAccountRequest) {
      const result = await updateUserSnsInfo(req);
      const commonStore = CommonStore();
      const findIndex = this._userSnsInfo.findIndex(
        (v) => v.media_type == req.media_type
      );
      const title = this._userSnsInfo[findIndex] ? "更新" : "登録";
      if (!result) {
        commonStore.setToast({
          showToast: true,
          message: `${
            MEDIA_NAME[req.media_type]
          }アカウントの${title}に失敗しました`,
          state: 1,
          timeout: 2000,
        });
      } else {
        commonStore.setToast({
          showToast: true,
          message: `${MEDIA_NAME[req.media_type]}アカウントを${title}しました`,
          state: 0,
          timeout: 2000,
        });
        await this.getUserSnsAccount();
      }
    },
    // SNS アカウント情報削除
    async deleteUserSnsAccount(req: number) {
      const commonStore = CommonStore();
      const result = await deleteUserSnsInfo(req);
      if (!result) {
        commonStore.setToast({
          showToast: true,
          message: `${MEDIA_NAME[req]}アカウント削除に失敗しました`,
          state: 1,
          timeout: 2000,
        });
      } else {
        commonStore.setToast({
          showToast: true,
          message: `${MEDIA_NAME[req]}アカウントを削除しました`,
          state: 0,
          timeout: 2000,
        });
        const updateSnsAccountList = this._userSnsInfo.filter(
          (v) => v.media_type != req
        );
        this.setUserSnsInfo(updateSnsAccountList);
      }
    },

    // 配送先住所取得
    async getAddressInfo() {
      const res = await getAddressInfo();
      this.setAddressInfo(res);
    },
    // 配送先住所更新
    async updateAddressInfo(
      req: addressRegistRequest
    ): Promise<addressResponse | undefined> {
      const commonStore = CommonStore();
      return await updateAddressInfo(req).then((res) => {
        if (res) {
          commonStore.setToast({
            showToast: true,
            message:
              document.title == "配送先住所更新"
                ? "配送先住所を更新しました。"
                : "配送先住所を登録しました。",
            state: 0,
            timeout: 2000,
          });
          // ストアの情報を更新しておく
          this.setAddressInfo(res);

          return res;
        } else {
          commonStore.setToast({
            showToast: true,
            message:
              document.title == "配送先住所更新"
                ? "配送先住所の更新に失敗しました。"
                : "配送先住所の登録に失敗しました。",
            state: 1,
            timeout: 2000,
          });

          return res;
        }
      });
    },
    // 配送先住所削除
    async deleteAddressInfo(): Promise<boolean> {
      const commonStore = CommonStore();
      return await deleteAddressInfo().then((res) => {
        if (res.status == 200) {
          commonStore.setToast({
            showToast: true,
            message: "配送先住所を削除しました。",
            state: 0,
            timeout: 2000,
          });
          // ストアの情報を更新しておく
          this.setAddressInfo(undefined);

          return true;
        } else {
          commonStore.setToast({
            showToast: true,
            message: res.error.message || "配送先住所の削除に失敗しました。",
            state: 1,
            timeout: 2000,
          });

          return false;
        }
      });
    },

    // setter
    setUserDetail(userDetail: userDetailResponse | undefined) {
      if (userDetail) this._user_detail = userDetail;
    },
    setbankInfo(res: bankInfoResponse | undefined) {
      if (res) this._bankInfo = res;
      else this._bankInfo = bankInfoResponse;
    },
    setUserSnsInfo(res: userSnsAccountResponse[] | undefined) {
      if (res) this._userSnsInfo = res;
      else this._userSnsInfo = [];
    },
    setIsRegistFromProjectDetail(val: {
      show: boolean;
      title: string;
      mediaType: number;
    }) {
      this._isRegistFromProjectDetail = val;
    },
    setAddressInfo(res: addressResponse | undefined) {
      if (res) this._addressInfo = res;
      else this._addressInfo = addressResponse;
    },
  },
});
